import React, { Component, Fragment } from "react";
import { observer, inject } from "mobx-react";
import { reaction } from "mobx";
import { Button } from "@material-ui/core";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";

import Header from "../../components/Header";
import Sidebar from "../../components/Sidebar";
import MainContainer from "../../components/MainContainer";

import Heading from "../../components/Heading";
import AgentRosterTable from "../../components/venue/agentRoster/AgentRosterTable";
import StaffAgentInvitationModal from "../../components/venue/agentRoster/StaffAgentInvitationModal";
import Loading from "../../components/loadingSpinner/Loading";

import { ProfileRelationType } from "../../types/enum";

@inject("templateStore", "agentVenueStore", "userInvitationStore", "profileStore")
@observer
class StaffAgentRoster extends Component {
    state = {
        invitationModalState: false,
        isLoading: true,
        loadError: null,
        venues: [],
        selectedVenueId: "all",
        staffProfileId: undefined
    };

    loadGeneration = 0;
    loadedStaffProfileId = undefined;
    profileChangeDisposer = undefined;
    isPageMounted = false;

    componentDidMount() {
        this.isPageMounted = true;
        this.profileChangeDisposer = reaction(
            () => {
                const { profileStore } = this.props;
                return profileStore.currentProfile?.id ??
                    profileStore.profile?.id ??
                    profileStore.getCurrentProfile()?.id;
            },
            profileId => {
                if (String(this.loadedStaffProfileId) !== String(profileId)) {
                    this.loadRoster();
                }
            }
        );
        this.loadRoster();
    }

    componentDidUpdate() {
        const currentProfileId = this.props.profileStore.getCurrentProfile()?.id;
        if (String(this.loadedStaffProfileId) !== String(currentProfileId)) {
            this.loadRoster();
        }
    }

    componentWillUnmount() {
        this.isPageMounted = false;
        this.loadGeneration += 1;
        if (this.profileChangeDisposer) {
            this.profileChangeDisposer();
            this.profileChangeDisposer = undefined;
        }
        this.props.agentVenueStore.clearStaffAgentRoster();
        this.props.agentVenueStore.resetInvitationField();
        this.props.userInvitationStore.resetInvitations();
    }

    loadRoster = () => {
        const generation = ++this.loadGeneration;
        const staffProfileId = this.props.profileStore.getCurrentProfile()?.id;
        this.loadedStaffProfileId = staffProfileId;
        this.props.agentVenueStore.clearStaffAgentRoster();
        this.props.agentVenueStore.resetInvitationField();
        this.props.userInvitationStore.resetInvitations();
        this.setState({
            invitationModalState: false,
            isLoading: true,
            loadError: null,
            venues: [],
            selectedVenueId: "all",
            staffProfileId
        });

        if (!staffProfileId) {
            this.setState({
                isLoading: false,
                loadError: "Select a staff profile to load its venue agents."
            });
            return Promise.resolve();
        }

        return Promise.all([
            this.props.agentVenueStore.loadStaffAgentRoster(staffProfileId),
            this.props.userInvitationStore.getMyUserInvitation(
                ProfileRelationType.AgentVenue.ordinal,
                staffProfileId
            )
        ]).then(([venues]) => {
            if (generation !== this.loadGeneration ||
                String(this.props.profileStore.getCurrentProfile()?.id) !== String(staffProfileId)) {
                return;
            }
            this.setState({
                isLoading: false,
                loadError: null,
                venues: Array.isArray(venues) ? venues : []
            });
        }).catch(() => {
            if (generation !== this.loadGeneration ||
                String(this.props.profileStore.getCurrentProfile()?.id) !== String(staffProfileId)) {
                return;
            }
            this.props.agentVenueStore.clearStaffAgentRoster();
            this.props.userInvitationStore.resetInvitations();
            this.setState({
                isLoading: false,
                loadError: "Unable to load agents and venues. Please try again."
            });
        });
    }

    openInvitationModal = () => {
        if (this.state.isLoading || this.state.loadError || this.state.venues.length === 0) return;
        this.setState(
            {
                ...this.state,
                invitationModalState: true
            },
            () => {
                this.props.agentVenueStore.resetInvitationField();
            }
        );
    };

    closeInvitationModal = () => {
        this.setState({
            ...this.state,
            invitationModalState: false
        });
    };

    handleVenueChange = event => {
        const selectedVenueId = String(event.target.value);
        if (selectedVenueId === "all" ||
            this.state.venues.some(venue => String(venue.id) === selectedVenueId)) {
            this.setState({ selectedVenueId });
        }
    };

    handleRetry = () => this.loadRoster();

    handlePostAcceptRefreshError = (error, actingProfileId) => {
        const currentProfileId = this.props.profileStore.getCurrentProfile()?.id;
        if (!this.isPageMounted ||
            !error?.requestAccepted ||
            actingProfileId == null ||
            String(actingProfileId) !== String(this.state.staffProfileId) ||
            String(actingProfileId) !== String(currentProfileId)) {
            return;
        }

        this.props.agentVenueStore.clearStaffAgentRoster();
        this.props.agentVenueStore.resetInvitationField();
        this.props.userInvitationStore.resetInvitations();
        this.setState({
            invitationModalState: false,
            isLoading: false,
            loadError: "The request was accepted, but the roster could not be refreshed. Please try again.",
            venues: [],
            selectedVenueId: "all"
        });
    };

    render() {
        const currentProfile = this.props.profileStore.getCurrentProfile();
        const allowBooking = !(currentProfile?.viewOnly) && !!currentProfile?.allowBooking;
        const { isLoading, loadError, venues, selectedVenueId, staffProfileId } = this.state;
        return (
            <Fragment>
                <Header />
                <Sidebar />
                <MainContainer>
                    <Heading title="My Agents" bottomAction={false}>
                        <div>
                        {
                            allowBooking ?
                            (
                                <Button
                                    color="primary"
                                    variant="contained"
                                    onClick={this.openInvitationModal}
                                    disabled={isLoading || !!loadError || venues.length === 0}
                                >
                                Add Agent
                                </Button>
                            )
                            :
                            ""
                        }
                           
                        </div>
                    </Heading>
                    {venues.length > 0 && !loadError && (
                        <div className="staff-agent-venue-filter">
                            <FormControl>
                                <InputLabel htmlFor="staff-agent-venue-select" id="staff-agent-venue-label">
                                    Viewing agents for
                                </InputLabel>
                                <Select
                                    native
                                    value={selectedVenueId}
                                    onChange={this.handleVenueChange}
                                    disabled={isLoading}
                                    inputProps={{
                                        id: "staff-agent-venue-select",
                                        name: "staffVenue",
                                        "aria-labelledby": "staff-agent-venue-label",
                                        "aria-label": "Viewing agents for"
                                    }}
                                >
                                    <option value="all">All My Venues ({venues.length})</option>
                                    {venues.map(venue => (
                                        <option key={venue.id} value={String(venue.id)}>
                                            {venue.fullName || venue.profileName}
                                        </option>
                                    ))}
                                </Select>
                            </FormControl>
                        </div>
                    )}
                    <div style={{ position: "relative" }}>
                        <Loading showed={isLoading} borderRadius={0} />
                        {!isLoading && loadError && (
                            <div className="venue-empty-state" role="alert">
                                <p>{loadError}</p>
                                <Button color="primary" variant="contained" onClick={this.handleRetry}>
                                    Retry
                                </Button>
                            </div>
                        )}
                        {!isLoading && !loadError && (
                            <AgentRosterTable
                                key={`${staffProfileId}:${selectedVenueId}`}
                                allowBooking={allowBooking}
                                venues={venues}
                                selectedVenueId={selectedVenueId}
                                profileId={staffProfileId}
                                onRefreshError={this.handlePostAcceptRefreshError}
                            />
                        )}
                    </div>
                    <StaffAgentInvitationModal
                        open={this.state.invitationModalState}
                        onClose={this.closeInvitationModal}
                        onSuccess={this.loadRoster}
                        availableVenues={venues}
                        profileId={staffProfileId}
                    />
                </MainContainer>
            </Fragment>
        );
    }
}

export default StaffAgentRoster;
